import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutApi } from "../../api/authApi";
import { logout as logoutAction } from "../../redux/sessionSlice";
import { hasCap, hasProduct } from "../../utils/capabilities";
import { base_name } from "../../config"; // eslint-disable-line no-unused-vars -- ileride basename route için
import logo from "../../assets/valonix-logo-beyaz.png";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";



export default function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const me = useSelector((s) => s.session?.me);

  const [showLogoutModal, setShowLogoutModal] = useState(false);


  const menuItems = [
    {
      id: "home",
      product: "iot",
      label: t("nav.menu.home"),
      icon: "home",
      path: "/home",
    },
    {
      id: "edges",
      product: "iot",
      label: t("nav.menu.edges"),
      icon: "devices",
      path: "/edges",
    },
    {
      id: "tenants",
      label: t("nav.menu.tenants"),
      icon: "business",
      path: "/tenants",
      requiredRole: ["superadmin", "admin"],
    },
    {
      id: "alarms",
      product: "iot",
      label: t("nav.menu.alarms"),
      icon: "notifications",
      path: "/alarms",
    },
    {
      id: "vpn",
      product: "iot",
      label: t("nav.menu.vpn"),
      icon: "vpn",
      path: "/vpn-users",
      requiredRole: ["superadmin", "admin"],
    },
    {
      id: "marketplace",
      product: "iot",
      label: t("nav.menu.marketplace"),
      icon: "store",
      path: "/marketplace",
      requiredRole: ["superadmin", "admin"],
    },
    {
      id: "edge-transfers",
      product: "iot",
      label: t("nav.menu.edge-transfers"),
      icon: "devices",
      path: "/edge-transfers",
      requiredCap: "edge.transfer",
    },
    {
      id: "ota-management",
      label: t("nav.menu.ota-management"),
      icon: "system_update",
      path: "/ota-management",
      requiredRole: ["superadmin"],
    },
    {
      id: "security",
      label: t("nav.menu.security"),
      icon: "security",
      path: "/security",
      requiredRole: ["superadmin"],
    },
    {
      id: "machine-sbom",
      product: "cra",
      label: t("nav.menu.machine-sbom"),
      icon: "security",
      path: "/machine-sbom",
      requiredCap: "security.sbom",
    },
    {
      id: "licenses",
      label: t("nav.menu.licenses"),
      icon: "key",
      path: "/licenses",
      requiredRole: ["superadmin"],
    },
    {
      id: "ssh-access",
      label: t("nav.menu.ssh-access"),
      icon: "terminal",
      path: "/ssh-access",
      requiredRole: ["superadmin"],
    },
    {
      id: "ai-settings",
      label: t("nav.menu.ai-settings"),
      icon: "rca",
      path: "/ai-settings",
      requiredRole: ["superadmin"],
    },
    {
      id: "rca",
      product: "iot",
      label: t("nav.menu.rca"),
      icon: "rca",
      path: "/rca",
      requiredRole: ["superadmin", "admin"],
      requiresRca: true,
    },
  ];

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (e) {
      // ignore
    } finally {
      localStorage.removeItem("user_info");
      dispatch(logoutAction());
      navigate("/", { replace: true });
    }
  };

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.requiresRca && !me?.rca_enabled) return false;
    if (item.product && !hasProduct(me, item.product)) return false; // a tenant sees only the products it holds
    if (me?.tenant_role === "operatör") {
      return item.id === "home";
    }
    if (item.requiredCap) return hasCap(me, item.requiredCap);
    if (!item.requiredRole) return true;
    const userRole = me?.global_role || me?.tenant_role;
    return item.requiredRole.includes(userRole);
  });

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <div style={{ ...styles.sidebar, width: collapsed ? 60 : 240 }}>
      {/* Logo & Toggle */}
      <div style={styles.header}>
        {!collapsed && (
          <img src={logo} alt={t("nav.logoAlt")} style={styles.logoImg} />
        )}
        <button style={styles.toggleBtn} onClick={onToggle}>
          {collapsed ? "→" : "←"}
        </button>
      </div>

      {/* Menu Items */}
      <nav style={styles.nav}>
        {filteredMenuItems.map((item) => (
          <button
            key={item.id}
            style={{
              ...styles.menuItem,
              ...(isActive(item.path) ? styles.menuItemActive : {}),
            }}
            onClick={() => navigate(item.path)}
            title={collapsed ? item.label : undefined}
          >
            <span style={styles.icon}>{getIcon(item.icon)}</span>
            {!collapsed && <span style={styles.label}>{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div style={styles.footer}>
        {!collapsed && (
          <div style={{ ...styles.userInfo, cursor: "pointer" }} onClick={() => navigate("/profile")} title={t("nav.profileTitle")}>
            <div style={styles.avatar}>
              {me?.full_name?.charAt(0)?.toUpperCase() || me?.email?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div style={styles.userDetails}>
              <span style={styles.userName}>{me?.full_name || me?.email}</span>
              <span style={styles.userRole}>{me?.tenant_role || me?.global_role}</span>
            </div>
          </div>
        )}
        <div style={{ margin: collapsed ? "8px 0" : "0 0 10px", display: "flex", justifyContent: "center" }}>
          <LanguageSwitcher variant="dark" compact={collapsed} block={!collapsed} />
        </div>
        {collapsed && (
          <button style={styles.logoutBtn} onClick={() => navigate("/profile")} title={t("nav.profileTitle")}>👤</button>
        )}
        <button style={styles.logoutBtn} onClick={() => setShowLogoutModal(true)} title={t("nav.logout")}>
          {collapsed ? "⏻" : t("nav.logout")}
        </button>
      </div>

      {showLogoutModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>{t("nav.logoutTitle")}</h3>
            <p style={styles.modalText}>{t("nav.logoutConfirm")}</p>
            <div style={styles.modalButtons}>
              <button style={styles.modalBtnYes} onClick={handleLogout}>{t("nav.yes")}</button>
              <button style={styles.modalBtnNo} onClick={() => setShowLogoutModal(false)}>{t("nav.no")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getIcon(name) {
  const icons = {
    home: "🏠",
    devices: "📟",
    business: "🏢",
    notifications: "🔔",
    vpn: "🔐",
    store: "🛒",
    security: "🛡️",
    key: "🔑",
    system_update: "⬆️",
    terminal: "⌘",
    rca: "🤖",
  };
  return icons[name] || "•";
}

const styles = {
  sidebar: {
    height: "100vh",
    backgroundColor: "var(--bg-sidebar)",
    color: "var(--text-sidebar)",
    display: "flex",
    flexDirection: "column",
    transition: "width 0.3s ease",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 1000,
    borderRight: "1px solid var(--border)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    borderBottom: "1px solid var(--border)",
  },
  logo: {
    fontSize: "18px",
    fontWeight: "bold",
    color: "var(--text-sidebar-active)",
  },
  logoImg: {
    height: "32px",
    objectFit: "contain",
  },
  toggleBtn: {
    background: "transparent",
    border: "none",
    color: "var(--toggle-btn-color)",
    fontSize: "16px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
  },
  nav: {
    flex: 1,
    padding: "16px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 16px",
    backgroundColor: "transparent",
    border: "none",
    color: "var(--text-sidebar)",
    fontSize: "14px",
    cursor: "pointer",
    borderRadius: "8px",
    textAlign: "left",
    transition: "all 0.2s",
  },
  menuItemActive: {
    backgroundColor: "var(--menu-active-bg)",
    color: "var(--menu-active-color)",
  },
  icon: {
    fontSize: "18px",
    width: "24px",
    textAlign: "center",
  },
  label: {
    whiteSpace: "nowrap",
  },
  footer: {
    padding: "16px",
    borderTop: "1px solid var(--border)",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "12px",
  },
  avatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "var(--avatar-bg)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "bold",
    color: "var(--avatar-color)",
  },
  userDetails: {
    display: "flex",
    flexDirection: "column",
  },
  userName: {
    fontSize: "14px",
    fontWeight: "500",
  },
  userRole: {
    fontSize: "12px",
    color: "#888",
  },
  logoutBtn: {
    width: "100%",
    padding: "10px",
    backgroundColor: "var(--menu-hover-bg)",
    border: "none",
    color: "var(--text-sidebar-active)",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "32px",
    minWidth: "340px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
  },
  modalTitle: {
    margin: "0 0 12px 0",
    fontSize: "20px",
    fontWeight: "600",
    color: "var(--text-primary)",
  },
  modalText: {
    margin: "0 0 24px 0",
    fontSize: "14px",
    color: "#555",
  },
  modalButtons: {
    display: "flex",
    gap: "12px",
  },
  modalBtnYes: {
    padding: "8px 24px",
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  modalBtnNo: {
    padding: "8px 24px",
    backgroundColor: "#e0e0e0",
    color: "#333",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
};
